import React, { useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";

/**
 * Uploader — a self-contained image uploader.
 * Props:
 *  - value: string | string[]         current file_id(s) already stored
 *  - onChange: (v) => void            called with new value(s) after upload
 *  - multiple?: boolean               allow multiple files (returns array)
 *  - max?: number                     max files if multiple (default 6)
 *  - testid?: string                  data-testid prefix
 *  - label?: string                   accessible label above the dropzone
 */
export default function Uploader({ value, onChange, multiple = false, max = 6, testid = "uploader", label }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const items = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const upload = async (files) => {
    setBusy(true);
    const newIds = [];
    try {
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} is over 5 MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newIds.push(data.id);
      }
      if (multiple) {
        const merged = [...items, ...newIds].slice(0, max);
        onChange(merged);
      } else if (newIds[0]) {
        onChange(newIds[0]);
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const onFileChange = (e) => upload(Array.from(e.target.files || []));
  const onDrop = (e) => { e.preventDefault(); e.stopPropagation(); upload(Array.from(e.dataTransfer.files || [])); };
  const remove = (id) => {
    if (multiple) onChange(items.filter((x) => x !== id));
    else onChange("");
  };

  const canAddMore = multiple ? items.length < max : items.length === 0;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const urlFor = (id) => `${backendUrl}/api/files/${id}`;

  return (
    <div data-testid={testid}>
      {label && <div className="mb-1 text-sm font-bold uppercase tracking-wide text-sarthi-muted">{label}</div>}
      {items.length > 0 && (
        <div className={`mb-2 grid ${multiple ? "grid-cols-3 gap-2 sm:grid-cols-4" : "grid-cols-1"}`}>
          {items.map((id) => (
            <div key={id} data-testid={`${testid}-preview-${id}`} className="group relative overflow-hidden rounded-xl border-2 border-sarthi-border bg-white">
              <img src={urlFor(id)} alt="" className={`${multiple ? "aspect-square" : "h-32"} w-full object-cover`} />
              <button
                type="button"
                data-testid={`${testid}-remove-${id}`}
                onClick={() => remove(id)}
                className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white opacity-0 transition group-hover:opacity-100"
                aria-label="Remove"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {canAddMore && (
        <label
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={onDrop}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-sarthi-border bg-sarthi-accent/30 px-4 py-6 text-sarthi-muted hover:border-sarthi-primary hover:text-sarthi-primary"
        >
          <input
            ref={inputRef}
            data-testid={`${testid}-input`}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple={multiple}
            className="hidden"
            onChange={onFileChange}
          />
          {busy ? (
            <><Loader2 className="h-6 w-6 animate-spin" /><span className="text-sm font-bold">Uploading…</span></>
          ) : (
            <>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sarthi-primary/10 text-sarthi-primary"><Upload className="h-5 w-5" /></div>
              <span className="text-sm font-bold">Click or drop {multiple ? `up to ${max} images` : "an image"}</span>
              <span className="text-xs">JPG, PNG, WebP, GIF · max 5 MB</span>
            </>
          )}
        </label>
      )}

      {!canAddMore && multiple && items.length >= max && (
        <div className="rounded-xl border-2 border-dashed border-sarthi-border bg-sarthi-accent/20 px-4 py-3 text-center text-xs font-bold uppercase tracking-wide text-sarthi-muted">
          <ImageIcon className="mx-auto mb-1 h-4 w-4" /> Max {max} images
        </div>
      )}
    </div>
  );
}
